// frontend/src/components/dashboard/FunilDeVendas.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Briefcase,
  CalendarCheck,
  CheckCircle2,
  ArrowDownRight,
  Target,
  TrendingDown,
  Sparkles,
} from 'lucide-react';

/* =======================
   PALETA 02 — AURORA
======================= */
const AURORA = {
  emerald: '#34D399',
  purple: '#A78BFA',
  amber: '#F59E0B',
  sky: '#38BDF8',
  border: 'rgba(255,255,255,0.10)',
  text: 'rgba(255,255,255,0.92)',
  text2: 'rgba(255,255,255,0.65)',
  text3: 'rgba(255,255,255,0.45)',
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function pct(a, b) {
  const A = Number(a || 0);
  const B = Number(b || 0);
  if (A <= 0 || B <= 0) return 0;
  return Math.max(0, Math.min(100, (B / A) * 100));
}

function labelPeriodo(mode) {
  if (mode === 'semestral') return 'Semestral';
  if (mode === 'anual') return 'Anual';
  return 'Trimestral';
}

/**
 * ✅ Props relevantes:
 * - loading
 * - totalNegocios (NO PERÍODO do funil)
 * - visitasRealizadas (NO PERÍODO do funil)
 * - totalContratos (NO PERÍODO do funil)
 *
 * ✅ Contexto:
 * - year: número (ex: 2026) -> para assinar o funil ao ANO da dashboard
 *
 * ✅ Filtro dentro do funil:
 * - periodoInicial: 'trimestral' | 'semestral' | 'anual'
 * - onPeriodoChange: (p) => void
 */
export default function FunilDeVendas({
  loading,
  totalNegocios = 0,
  visitasRealizadas = 0,
  totalContratos = 0,

  year, // ✅ opcional (apenas rótulo)
  compact = false,
  dark = true,

  // ✅ filtro dentro do funil
  periodoInicial = 'trimestral',
  onPeriodoChange,
}) {
  // ✅ HOOKS SEMPRE EXECUTAM
  const [periodo, setPeriodo] = useState(periodoInicial);

  // ✅ sincroniza se o pai mudar periodoInicial (safe)
  useEffect(() => {
    setPeriodo(periodoInicial);
  }, [periodoInicial]);

  const ui = useMemo(() => {
    const a = Number(totalNegocios || 0);
    const b = Number(visitasRealizadas || 0);
    const c = Number(totalContratos || 0);

    const max = Math.max(1, a, b, c);

    // largura mínima alta mantém estética (mesmo com poucos dados)
    const wA = clamp((a / max) * 100, 64, 100);
    const wB = clamp((b / max) * 100, 52, 90);
    const wC = clamp((c / max) * 100, 44, 80);

    const n2v = pct(a, b);
    const v2c = pct(b, c);

    // ✅ Gargalo inteligente
    const weakN2V = a > 0 && n2v > 0 && n2v < 30;
    const weakV2C = b > 0 && v2c > 0 && v2c < 25;

    const stepFlags = {
      semVisitas: a > 0 && b === 0,
      semContratos: b > 0 && c === 0,
      gargaloN2V: weakN2V || (a > 0 && b === 0),
      gargaloV2C: weakV2C || (b > 0 && c === 0),
      inconsistente: c > b && b >= 0, // fechamentos > visitas
    };

    // ✅ Insight coerente com Elion (simples e acionável)
    let insight = {
      icon: Sparkles,
      title: 'Ritmo saudável',
      text: 'Seu funil está consistente. Mantenha o registro das ações para sustentar a performance.',
      tone: 'ok',
    };

    if (a === 0) {
      insight = {
        icon: Target,
        title: 'Comece pelo topo',
        text: 'Você ainda não registrou negócios no período. Cadastre seus atendimentos para medir seu ritmo real.',
        tone: 'warn',
      };
    } else if (stepFlags.inconsistente) {
      insight = {
        icon: TrendingDown,
        title: 'Dados inconsistentes',
        text: 'Há mais fechamentos do que visitas registradas. Revise os registros do período para o funil refletir sua rotina.',
        tone: 'warn',
      };
    } else if (stepFlags.semVisitas) {
      insight = {
        icon: Target,
        title: 'Gargalo: Negócios → Visitas',
        text: 'Você tem negócios no período, mas nenhuma visita registrada. Priorize agendamentos e registre cada visita.',
        tone: 'warn',
      };
    } else if (stepFlags.semContratos) {
      insight = {
        icon: Target,
        title: 'Gargalo: Visitas → Fechamentos',
        text: 'Você tem visitas no período, mas nenhum fechamento registrado. Intensifique follow-up e conduza propostas com clareza.',
        tone: 'warn',
      };
    } else if (stepFlags.gargaloN2V) {
      insight = {
        icon: Target,
        title: 'Conversão baixa: Negócios → Visitas',
        text: `Sua conversão está em ${n2v.toFixed(
          0
        )}%. Qualifique melhor e já saia do atendimento com visita marcada.`,
        tone: 'warn',
      };
    } else if (stepFlags.gargaloV2C) {
      insight = {
        icon: Target,
        title: 'Conversão baixa: Visitas → Fechamentos',
        text: `Sua conversão está em ${v2c.toFixed(
          0
        )}%. Trabalhe proposta, objeções e sequência de follow-up nas próximas 48h.`,
        tone: 'warn',
      };
    }

    const etapas = [
      {
        key: 'negocios',
        label: 'Negócios criados',
        value: a,
        Icon: Briefcase,
        width: wA,
        hint: 'Topo do funil (no período)',
        accent: 'emerald',
      },
      {
        key: 'visitas',
        label: 'Visitas realizadas',
        value: b,
        Icon: CalendarCheck,
        width: wB,
        hint: `Conversão: ${n2v.toFixed(0)}%`,
        accent: 'mix',
      },
      {
        key: 'contratos',
        label: 'Fechamentos',
        value: c,
        Icon: CheckCircle2,
        width: wC,
        hint: `Conversão: ${v2c.toFixed(0)}%`,
        accent: 'purple',
      },
    ];

    const dica =
      'Conta apenas negócios criados, visitas e fechamentos no período selecionado.';

    return { etapas, insight, flags: stepFlags, dica, n2v, v2c };
  }, [totalNegocios, visitasRealizadas, totalContratos]);

  const pad = compact ? 'p-5' : 'p-6';

  const shell = dark
    ? 'rounded-3xl bg-zinc-950/80 shadow-2xl backdrop-blur border'
    : 'rounded-3xl border border-zinc-200 bg-white shadow-sm';

  const titleCls = dark ? 'text-white' : 'text-zinc-950';
  const subCls = dark ? 'text-white/70' : 'text-zinc-600';
  const connectorClr = dark ? AURORA.text3 : 'rgba(113,113,122,0.7)';

  const getAccentLine = (accent) => {
    if (accent === 'emerald')
      return `linear-gradient(90deg, ${AURORA.emerald}, rgba(52,211,153,0.15))`;
    if (accent === 'purple')
      return `linear-gradient(90deg, ${AURORA.purple}, rgba(167,139,250,0.15))`;
    return `linear-gradient(90deg, ${AURORA.emerald}, ${AURORA.purple})`;
  };

  const getIconBg = (accent) => {
    if (accent === 'emerald')
      return 'linear-gradient(135deg, rgba(52,211,153,0.18), rgba(52,211,153,0.06))';
    if (accent === 'purple')
      return 'linear-gradient(135deg, rgba(167,139,250,0.18), rgba(167,139,250,0.06))';
    return 'linear-gradient(135deg, rgba(52,211,153,0.14), rgba(167,139,250,0.12))';
  };

  const periodos = [
    { key: 'trimestral', label: 'Trimestral' },
    { key: 'semestral', label: 'Semestral' },
    { key: 'anual', label: 'Anual' },
  ];

  const handlePeriodo = (p) => {
    setPeriodo(p);
    if (onPeriodoChange) onPeriodoChange(p);
  };

  // ✅ Skeleton DEPOIS dos hooks
  if (loading) {
    return (
      <div
        className={`animate-pulse rounded-3xl border backdrop-blur ${
          dark ? 'bg-zinc-950/80' : 'bg-white'
        } ${compact ? 'h-[300px]' : 'h-[320px]'}`}
        style={dark ? { borderColor: AURORA.border } : undefined}
      />
    );
  }

  const periodoLabel = labelPeriodo(periodo);
  const anoLabel =
    Number.isFinite(Number(year)) && String(year).length === 4
      ? String(year)
      : null;

  const InsightIcon = ui.insight.icon;

  const insightTone =
    ui.insight.tone === 'warn'
      ? {
          bg: 'rgba(245,158,11,0.12)',
          border: 'rgba(245,158,11,0.22)',
          text: '#FCD34D',
          title: 'rgba(255,255,255,0.92)',
        }
      : {
          bg: 'rgba(52,211,153,0.10)',
          border: 'rgba(52,211,153,0.18)',
          text: 'rgba(167,243,208,0.95)',
          title: 'rgba(255,255,255,0.92)',
        };

  return (
    <section
      className={`${shell} ${pad} h-full`}
      style={dark ? { borderColor: AURORA.border } : undefined}
    >
      {/* HEADER + FILTRO */}
      <div className={compact ? 'mb-4' : 'mb-5'}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              className={`${compact ? 'text-base' : 'text-lg'} font-semibold ${titleCls}`}
            >
              Funil de Vendas
            </div>

            {/* ✅ assinatura de contexto */}
            <div className={`${compact ? 'text-xs' : 'text-sm'} ${subCls}`}>
              Ritmo de conversão <span className="text-white/40">—</span>{' '}
              {anoLabel ? (
                <>
                  <span className="text-white/85 font-semibold">
                    {anoLabel}
                  </span>{' '}
                  <span className="text-white/40">•</span>{' '}
                  <span className="text-white/70">{periodoLabel}</span>
                </>
              ) : (
                <span className="text-white/70">{periodoLabel}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {periodos.map((p) => {
              const active = periodo === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handlePeriodo(p.key)}
                  className="rounded-xl px-3 py-1.5 text-[11px] font-semibold transition border"
                  style={{
                    borderColor: active
                      ? 'rgba(99,102,241,0.35)'
                      : AURORA.border,
                    background: active
                      ? 'rgba(99,102,241,0.14)'
                      : 'rgba(255,255,255,0.04)',
                    color: active ? 'rgba(255,255,255,0.92)' : AURORA.text2,
                  }}
                  title={`Ver ${p.label}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ✅ insight acionável */}
        <div
          className="mt-3 rounded-2xl border px-3.5 py-3 backdrop-blur"
          style={{
            background: insightTone.bg,
            borderColor: insightTone.border,
          }}
        >
          <div className="flex items-start gap-2.5">
            <div
              className="grid h-9 w-9 place-items-center rounded-2xl border"
              style={{
                borderColor: insightTone.border,
                background: 'rgba(255,255,255,0.04)',
                color: insightTone.text,
              }}
            >
              <InsightIcon className="h-4.5 w-4.5" />
            </div>

            <div className="min-w-0">
              <div
                className="text-[12px] font-semibold"
                style={{ color: insightTone.title }}
              >
                {ui.insight.title}
              </div>
              <div className="text-[11px]" style={{ color: AURORA.text2 }}>
                {ui.insight.text}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ETAPAS */}
      <div className={compact ? 'space-y-3.5' : 'space-y-4'}>
        {ui.etapas.map((e, idx) => {
          const isVisitas = e.key === 'visitas';
          const isContratos = e.key === 'contratos';

          const showGargalo =
            (isVisitas && ui.flags.gargaloN2V) ||
            (isContratos && ui.flags.gargaloV2C);

          return (
            <div key={e.key} className="flex flex-col items-center">
              <div
                className="relative overflow-hidden rounded-3xl border backdrop-blur"
                style={{
                  width: `${e.width}%`,
                  borderColor: AURORA.border,
                  background: 'rgba(255,255,255,0.05)',
                  clipPath: 'polygon(0 0, 100% 0, 94% 100%, 6% 100%)',
                }}
              >
                {/* halos aurora */}
                <div
                  className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full blur-3xl"
                  style={{ background: 'rgba(167,139,250,0.22)' }}
                />
                <div
                  className="pointer-events-none absolute -left-10 -bottom-10 h-44 w-44 rounded-full blur-3xl"
                  style={{ background: 'rgba(52,211,153,0.18)' }}
                />

                {/* linha superior aurora */}
                <div
                  className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
                  style={{ background: getAccentLine(e.accent) }}
                />

                <div className={`relative ${compact ? 'p-3.5' : 'p-4'}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`grid place-items-center rounded-2xl border ${
                          compact ? 'h-9 w-9' : 'h-10 w-10'
                        }`}
                        style={{
                          borderColor: AURORA.border,
                          background: getIconBg(e.accent),
                          color: AURORA.text,
                        }}
                      >
                        <e.Icon
                          className={compact ? 'h-4.5 w-4.5' : 'h-5 w-5'}
                        />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div
                            className={`${compact ? 'text-[13px]' : 'text-sm'} font-semibold`}
                            style={{ color: AURORA.text }}
                          >
                            {e.label}
                          </div>

                          {showGargalo && (
                            <span
                              className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                              style={{
                                background: 'rgba(245,158,11,0.15)',
                                color: '#FCD34D',
                                border: '1px solid rgba(245,158,11,0.25)',
                              }}
                            >
                              Gargalo
                            </span>
                          )}
                        </div>

                        <div
                          className="text-[11px]"
                          style={{ color: AURORA.text2 }}
                        >
                          {e.hint}
                        </div>
                      </div>
                    </div>

                    <div
                      className={`${compact ? 'text-xl' : 'text-2xl'} font-extrabold`}
                      style={{ color: AURORA.text }}
                    >
                      {e.value}
                    </div>
                  </div>
                </div>
              </div>

              {/* conector */}
              {idx < ui.etapas.length - 1 && (
                <div
                  className={
                    compact
                      ? 'mt-2.5 flex items-center gap-2'
                      : 'mt-3 flex items-center gap-2'
                  }
                >
                  <ArrowDownRight
                    className="h-4 w-4"
                    style={{ color: connectorClr }}
                  />
                  <div className="text-[11px]" style={{ color: AURORA.text2 }}>
                    De {ui.etapas[idx].label} → {ui.etapas[idx + 1].label}:{' '}
                    <span
                      className="font-semibold"
                      style={{ color: AURORA.text }}
                    >
                      {pct(
                        ui.etapas[idx].value,
                        ui.etapas[idx + 1].value
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* rodapé */}
      <div className="mt-4 text-[11px]" style={{ color: AURORA.text2 }}>
        {ui.dica}
      </div>
    </section>
  );
}
